import { useEffect, useRef, useState } from 'react';
import type { MatchChatPayload, Side } from '@xiangqi/protocol';

interface ChatDrawerProps {
  open: boolean;
  onClose(): void;
  messages: MatchChatPayload[];
  onSendMessage(text: string): void;
  yourSide: Side | null;
}

const QUICK_CHATS = [
  'Chào bạn! 👋',
  'Nhanh lên bạn ơi ⏱️',
  'Nước cờ hay quá! 👏',
  'Đánh hay lắm 👍',
  'Cảm ơn bạn! 😊',
  'Thua phục rồi! 🤝',
  'Chúc may mắn! 🍀',
  'Một ván nữa nhé? 🔄',
];

export function ChatDrawer({
  open,
  onClose,
  messages,
  onSendMessage,
  yourSide,
}: ChatDrawerProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  if (!open) return null;

  const handleSend = (textToSend?: string) => {
    const text = (textToSend ?? input).trim();
    if (!text) return;
    onSendMessage(text);
    if (!textToSend) setInput('');
  };

  return (
    <div className="chat-drawer-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="chat-drawer" role="dialog" aria-label="Trò chuyện trận đấu">
        <header className="chat-header">
          <div className="chat-header-title">
            <span className="chat-icon">💬</span>
            <h3>Trò chuyện</h3>
            <span className="chat-count">({messages.length})</span>
          </div>
          <button className="chat-close-btn" onClick={onClose} aria-label="Đóng chat">
            ✕
          </button>
        </header>

        {/* Quick chat suggestions */}
        <div className="quick-chat-chips">
          {QUICK_CHATS.map((qc, idx) => (
            <button
              key={idx}
              className="quick-chip"
              onClick={() => handleSend(qc)}
            >
              {qc}
            </button>
          ))}
        </div>

        {/* Message list */}
        <div className="chat-messages-scroll">
          {messages.length === 0 ? (
            <div className="chat-empty">
              <p>Chưa có tin nhắn nào.</p>
              <p className="hint">Gửi lời chào hoặc chọn một câu thoại nhanh!</p>
            </div>
          ) : (
            messages.map((m, idx) => {
              const isYou = m.side === yourSide && yourSide !== null;
              const timeStr = new Date(m.created_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              });
              const sideLabel =
                m.side === 'red' ? 'Đỏ' : m.side === 'black' ? 'Đen' : 'Khán giả';

              return (
                <div
                  key={idx}
                  className={`chat-bubble-row ${isYou ? 'you' : 'other'} ${m.side ?? 'spec'}`}
                >
                  <div className="chat-bubble">
                    <div className="chat-bubble-sender">
                      <span className={`side-tag ${m.side ?? 'spec'}`}>{sideLabel}</span>
                      <strong className="sender-name">{m.sender_name}</strong>
                      <span className="bubble-time">{timeStr}</span>
                    </div>
                    <div className="bubble-text">{m.text}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input box */}
        <form
          className="chat-input-row"
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
        >
          <input
            type="text"
            className="chat-input"
            placeholder="Chat với đối phương..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={150}
            autoFocus
          />
          <button
            type="submit"
            className="chat-send-btn"
            disabled={!input.trim()}
          >
            Gửi
          </button>
        </form>
      </div>
    </div>
  );
}
